import { useEffect, useState } from "react"
import { useParams, useNavigate, useSearchParams, Link } from "react-router"
import { api, ApiError } from "../../lib/api"
import appName from "../../lib/appName"
import "../../styles/auth.scss"

type InvitationData = {
  organization_name: string
  invited_by: string
  email: string
  role: string
  expires_at: string
  accepted: boolean
  expired: boolean
}

type AuthStatus = "loading" | "authenticated" | "unauthenticated"
type CurrentUser = { id: number; email: string }
type CurrentUserResponse = { user: CurrentUser; has_organization: boolean }

const ROLE_LABELS: Record<string, string> = {
  member: "メンバー",
  owner: "オーナー",
}

const InvitationAcceptPage = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const oauthError = searchParams.get("oauth_error")
  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [, setCurrentUser] = useState<CurrentUser | null>(null)
  const [authStatus, setAuthStatus] = useState<AuthStatus>("loading")
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    sessionStorage.removeItem("oauth_post_redirect")
    Promise.all([
      api.get<InvitationData>(`/invitations/${token}`).catch(() => null),
      api.get<CurrentUserResponse>("/users/me").catch(() => null),
    ]).then(async ([inv, userResponse]) => {
      if (!inv) {
        setNotFound(true)
      } else {
        setInvitation(inv)
      }
      const user = userResponse?.user ?? null

      if (oauthError) {
        // OAuthメールアドレス不一致: ログアウトしてエラー表示
        if (user) await api.delete("/users/sign_out").catch(() => {})
        setAuthStatus("unauthenticated")
        setError("招待されたメールアドレスと異なるアカウントでログインしようとしました。招待されたメールアドレスのアカウントでログインし直してください。")
      } else if (user && inv && user.email !== inv.email) {
        await api.delete("/users/sign_out").catch(() => {})
        setAuthStatus("unauthenticated")
        setError("招待されたメールアドレスのアカウントでログインし直してください。")
      } else {
        setCurrentUser(user)
        setAuthStatus(user ? "authenticated" : "unauthenticated")
      }
    })
  }, [token, oauthError])

  const handleAccept = async () => {
    setError(null)
    setAccepting(true)
    try {
      await api.post(`/invitations/${token}/accept`, {})
      navigate("/")
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "参加処理に失敗しました")
    } finally {
      setAccepting(false)
    }
  }

  if (authStatus === "loading") return null

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.png" alt={appName()} className="auth-logo__img" />
        </div>

        {notFound ? (
          <>
            <h1 className="auth-title">招待リンクが見つかりません</h1>
            <p className="auth-subtitle">このリンクは無効か、すでに削除されています</p>
          </>
        ) : invitation?.accepted ? (
          <>
            <h1 className="auth-title">この招待はすでに使用済みです</h1>
            <p className="auth-subtitle">別のアカウントで参加済みです</p>
          </>
        ) : invitation?.expired ? (
          <>
            <h1 className="auth-title">招待リンクの有効期限切れです</h1>
            <p className="auth-subtitle">管理者に再度招待を依頼してください</p>
          </>
        ) : invitation ? (
          <>
            <h1 className="auth-title">組織への招待</h1>
            <p className="auth-subtitle">
              <strong>{invitation.invited_by}</strong> さんから<br />
              <strong>{invitation.organization_name}</strong> への招待が届いています
            </p>

            <div className="invite-accept-info">
              <div className="invite-accept-info__row">
                <span>ロール</span>
                <span>{ROLE_LABELS[invitation.role] ?? invitation.role}</span>
              </div>
              <div className="invite-accept-info__row">
                <span>有効期限</span>
                <span>{invitation.expires_at}</span>
              </div>
            </div>

            {error && <div className="error-banner">{error}</div>}

            {authStatus === "authenticated" ? (
              <button
                className="btn-primary"
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? "参加中..." : "組織に参加する"}
              </button>
            ) : (
              <div className="invite-accept-auth">
                <p>参加するにはログインが必要です</p>
                <Link to={`/login?redirect=${encodeURIComponent(`/invitations/${token}`)}`} className="btn-primary">
                  ログインして参加する
                </Link>
                <Link to={`/signup?redirect=${encodeURIComponent(`/invitations/${token}`)}`} className="btn-oauth">
                  アカウントを作成して参加する
                </Link>
              </div>
            )}
          </>
        ) : null}

        <div className="auth-footer">
          <button
            className="auth-footer__link"
            onClick={async () => {
              await api.delete("/users/sign_out").catch(() => {})
              navigate(`/login?redirect=${encodeURIComponent(`/invitations/${token}`)}`)
            }}
          >
            ログインページへ戻る
          </button>
        </div>
      </div>
    </div>
  )
}

export default InvitationAcceptPage
